import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Upload, Plus, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CaseStudyPortal = () => {
  const [formData, setFormData] = useState({
    title: '',
    client_name: '',
    industry: '',
    platform: '',
    challenge: '',
    solution: '',
    results: [
      { metric: 'Leads Generated', value: '' },
      { metric: 'ROI Increase', value: '' },
      { metric: 'Cost Reduction', value: '' },
    ],
    testimonial: '',
    image_url: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleResultChange = (index, field, value) => {
    const newResults = [...formData.results];
    newResults[index][field] = value;
    setFormData({ ...formData, results: newResults });
  };

  const addResult = () => {
    setFormData({
      ...formData,
      results: [...formData.results, { metric: '', value: '' }],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/case-study`, formData);
      toast.success('Case study submitted successfully!');
      setSubmitted(true);
      setFormData({
        title: '',
        client_name: '',
        industry: '',
        platform: '',
        challenge: '',
        solution: '',
        results: [
          { metric: 'Leads Generated', value: '' },
          { metric: 'ROI Increase', value: '' },
          { metric: 'Cost Reduction', value: '' },
        ],
        testimonial: '',
        image_url: '',
      });
    } catch (error) {
      toast.error('Failed to submit case study. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="case-study-portal">
      <Header />

      <div className="pt-32 pb-20 bg-slate-50 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold mb-8"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </Link>

          <div className="bg-white rounded-3xl shadow-lg p-8 md:p-12">
            {!submitted ? (
              <>
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center">
                    <Upload className="w-8 h-8 text-teal-600" />
                  </div>
                  <div>
                    <h1 className="text-4xl font-black text-slate-900">Submit Case Study</h1>
                    <p className="text-slate-600 mt-1">Showcase your latest client success</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Case Study Title *
                      </label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        required
                        placeholder="e.g., Real Estate Lead Generation - Noida"
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                        data-testid="case-study-title-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Client Name *
                      </label>
                      <input
                        type="text"
                        value={formData.client_name}
                        onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                        required
                        placeholder="Client or Company Name"
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                        data-testid="case-study-client-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Industry *
                      </label>
                      <select
                        value={formData.industry}
                        onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                        data-testid="case-study-industry-select"
                      >
                        <option value="">Select Industry</option>
                        <option>Real Estate</option>
                        <option>Healthcare</option>
                        <option>E-commerce</option>
                        <option>Education</option>
                        <option>Hospitality</option>
                        <option>Technology</option>
                        <option>Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Platform *
                      </label>
                      <select
                        value={formData.platform}
                        onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                        data-testid="case-study-platform-select"
                      >
                        <option value="">Select Platform</option>
                        <option>Meta Ads (Facebook & Instagram)</option>
                        <option>Google Ads</option>
                        <option>LinkedIn Ads</option>
                        <option>Social Media Management</option>
                        <option>SEO</option>
                        <option>Multi-Channel</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Challenge / Problem *
                    </label>
                    <textarea
                      value={formData.challenge}
                      onChange={(e) => setFormData({ ...formData, challenge: e.target.value })}
                      required
                      rows="3"
                      placeholder="What was the client's main challenge or problem?"
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                      data-testid="case-study-challenge-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Solution / Strategy *
                    </label>
                    <textarea
                      value={formData.solution}
                      onChange={(e) => setFormData({ ...formData, solution: e.target.value })}
                      required
                      rows="4"
                      placeholder="Describe the marketing strategy and solutions you implemented"
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                      data-testid="case-study-solution-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-3">
                      Results & Metrics *
                    </label>
                    {formData.results.map((result, index) => (
                      <div key={`result-${index}-${result.metric}`} className="grid md:grid-cols-2 gap-4 mb-3">
                        <input
                          type="text"
                          value={result.metric}
                          onChange={(e) => handleResultChange(index, 'metric', e.target.value)}
                          required
                          placeholder="Metric (e.g., Leads Generated)"
                          className="px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                        <input
                          type="text"
                          value={result.value}
                          onChange={(e) => handleResultChange(index, 'value', e.target.value)}
                          required
                          placeholder="Value (e.g., 800+)"
                          className="px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addResult}
                      className="text-teal-600 hover:text-teal-700 font-semibold flex items-center gap-2 mt-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Another Metric
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Client Testimonial (Optional)
                    </label>
                    <textarea
                      value={formData.testimonial}
                      onChange={(e) => setFormData({ ...formData, testimonial: e.target.value })}
                      rows="3"
                      placeholder="Client's feedback or testimonial"
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                      data-testid="case-study-testimonial-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Image URL (Optional)
                    </label>
                    <input
                      type="url"
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      placeholder="https://example.com/image.jpg"
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      data-testid="case-study-image-input"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-teal-500 hover:bg-teal-600 text-white px-6 py-4 rounded-xl font-bold transition-colors disabled:opacity-50"
                    data-testid="case-study-submit-btn"
                  >
                    {loading ? 'Submitting...' : 'Submit Case Study'}
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center py-12">
                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-12 h-12 text-green-600" />
                </div>
                <h2 className="text-3xl font-bold text-slate-900 mb-4">Case Study Submitted!</h2>
                <p className="text-slate-600 mb-6">
                  Your case study has been successfully submitted and will be reviewed shortly.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-3 rounded-xl font-bold transition-colors"
                >
                  Submit Another
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CaseStudyPortal;
